/*
 * cx-photo-customiser.js -- controller for the cx-photo customised products:
 *   data-cx-mode="frame"  -> Your Lifeline Photo Frame for Your Phone
 *   data-cx-mode="sketch" -> Your Photo as a Charcoal Sketch
 *
 * Flow: upload -> aspect-locked crop (auto, adjustable) -> live preview
 * (frame + caption + optional effect, or sketch style) -> sizes -> one
 * flattened PNG at >= 300 dpi for the largest selected size, uploaded as the
 * `Custom Image` property via multipart /cart/add.js (same mechanism as the
 * live Customise Your Own Image / QR products). Cart flow forked from
 * custom-personalization-qr.js; no shared file is modified or loaded.
 *
 * Properties emitted (none of them http-leading except Custom Image):
 *   frame : Custom Image, Frame, Caption, Caption Font, Effect
 *   sketch: Custom Image, Sketch Style
 */
(function () {
  'use strict';

  var PRINT_DPI = 300;
  var MAX_FILE_BYTES = 20 * 1024 * 1024;
  var MAX_DIMENSION_PX = 8000;
  var SOURCE_CAP = 1600; // longest side kept after crop (6" photo window = 1315px)
  var PREVIEW_FRAME_H = 820;
  var PREVIEW_SKETCH_H = 700;
  var SKETCH_PREVIEW_WORK = 560;
  var SKETCH_FINAL_WORK = 900;

  var EFFECT_COPY = {
    None: 'Your photo as it is.',
    'Retro Film': 'Warm film colours, soft grain and a gentle light leak, like an old family print.',
    'Oil Painting': 'Repainted with real brush strokes, like a portrait on canvas.',
  };
  var BUSY_COPY = {
    None: 'Framing your photo...',
    'Retro Film': 'Developing your film...',
    'Oil Painting': 'Painting your photo...',
    sketch: 'Sketching your photo...',
  };

  function nextFrame() {
    return new Promise(function (resolve) {
      requestAnimationFrame(function () {
        setTimeout(resolve, 16);
      });
    });
  }

  function init(root) {
    if (!window.CXPhotoFX || !window.CXPhotoCompose) return;
    var FX = window.CXPhotoFX;
    var CMP = window.CXPhotoCompose;
    var mode = root.getAttribute('data-cx-mode') === 'sketch' ? 'sketch' : 'frame';
    var ASPECT = mode === 'frame' ? 1 : CMP.SKETCH_ASPECT; // crop width / height

    var $ = function (sel) {
      return root.querySelector(sel);
    };
    var fileInput = $('[data-cx-file]');
    var drop = $('[data-cx-drop]');
    var dropLabel = $('[data-cx-drop-label]');
    var fileErr = $('[data-cx-file-error]');
    var fileErrText = $('[data-cx-file-error-text]');
    var photoActions = $('[data-cx-photo-actions]');
    var changePhoto = $('[data-cx-change-photo]');
    var previewWrap = $('[data-cx-preview-wrap]');
    var stage = $('[data-cx-stage]');
    var canvas = $('[data-cx-canvas]');
    var prefillImg = $('[data-cx-prefill-img]');
    var busy = $('[data-cx-busy]');
    var busyText = $('[data-cx-busy-text]');
    var arImg = $('[data-cx-ar-img]');
    var styleStep = $('[data-cx-step="style"]');
    var effectStep = $('[data-cx-step="effect"]');
    var captionInput = $('[data-cx-caption]');
    var captionCount = $('[data-cx-caption-count]');
    var effectDesc = $('[data-cx-effect-desc]');
    var regenBtn = $('[data-cx-regen]');
    var regenText = $('[data-cx-regen-text]');
    var styleLabel = $('[data-cx-style-label]');

    var submitButton = $('[data-custom-personalization-submit]');
    var submitButtonText = $('[data-custom-personalization-submit-text]');
    var errorWrapper = $('[data-custom-personalization-error]');
    var errorText = $('[data-custom-personalization-error-text]');
    var primaryQtyInput = $('[data-primary-qty-input]');
    var openMoreSizesButton = $('[data-open-more-sizes]');
    var ownPrice = $('[data-custom-personalization-price]');
    var ownPriceNote = $('[data-custom-personalization-price-note]');
    var moreSizesModal = document.querySelector('#' + (openMoreSizesButton && openMoreSizesButton.closest('modal-opener') ? openMoreSizesButton.closest('modal-opener').getAttribute('data-modal').slice(1) : 'none'));

    var cropModal = document.querySelector(
      $('[data-cx-open-crop]') ? $('[data-cx-open-crop]').closest('modal-opener').getAttribute('data-modal') : '#none'
    );
    var cropImage = cropModal ? cropModal.querySelector('[data-cx-crop-image]') : null;
    var cropBoxEl = cropModal ? cropModal.querySelector('[data-cx-crop-box]') : null;
    var cropConfirm = cropModal ? cropModal.querySelector('[data-cx-crop-confirm]') : null;

    var state = {
      img: null,
      objectUrl: null,
      crop: null,
      src: null,
      frame: 'White',
      caption: captionInput ? captionInput.value : '',
      font: 'handwritten',
      effect: 'None',
      styleIdx: 0,
      seed: 1,
      processed: {}, // effect cache for the current source: key -> canvas
      prefillUrl: null,
      prefillEffectTier: false,
      busy: false,
      renderToken: 0,
    };

    /* ---------------- upload ---------------- */
    function showFileError(msg) {
      fileErrText.textContent = msg;
      fileErr.hidden = false;
      fileInput.value = '';
    }

    function defaultCrop(w, h) {
      var cw = w, ch = w / ASPECT;
      if (ch > h) {
        ch = h;
        cw = h * ASPECT;
      }
      // faces usually sit in the upper part of a portrait: bias upward
      return { x: (w - cw) / 2, y: (h - ch) * 0.3, w: cw, h: ch };
    }

    function buildSource() {
      var c = state.crop;
      var scale = Math.min(1, SOURCE_CAP / Math.max(c.w, c.h));
      state.src = FX.tagSource(FX.fitCanvas(state.img, Math.round(c.w * scale), Math.round(c.h * scale), c));
      state.processed = {};
    }

    fileInput.addEventListener('change', function () {
      var file = fileInput.files && fileInput.files[0];
      if (!file) return;
      fileErr.hidden = true;
      if (file.size > MAX_FILE_BYTES) {
        showFileError('That image is too large (max 20MB). Please choose a smaller file.');
        return;
      }
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        if (img.naturalWidth > MAX_DIMENSION_PX || img.naturalHeight > MAX_DIMENSION_PX) {
          showFileError("That image's dimensions are too large. Please choose a smaller file.");
          URL.revokeObjectURL(url);
          return;
        }
        if (img.naturalWidth < 200 || img.naturalHeight < 200) {
          showFileError('That photo is too small to print well. Please choose a bigger one.');
          URL.revokeObjectURL(url);
          return;
        }
        if (state.objectUrl) URL.revokeObjectURL(state.objectUrl);
        state.objectUrl = url;
        state.img = img;
        state.prefillUrl = null;
        state.prefillEffectTier = false;
        prefillImg.hidden = true;
        canvas.hidden = false;
        state.crop = defaultCrop(img.naturalWidth, img.naturalHeight);
        buildSource();
        drop.setAttribute('data-has-photo', '');
        dropLabel.textContent = 'Photo added. Tap to choose a different one';
        photoActions.hidden = false;
        previewWrap.hidden = false;
        if (styleStep) styleStep.hidden = false;
        if (effectStep) effectStep.hidden = false;
        render();
        refreshSubmitGate();
      };
      img.onerror = function () {
        showFileError('That file could not be read as an image. Please choose a JPG or PNG photo.');
        URL.revokeObjectURL(url);
      };
      img.src = url;
    });

    if (changePhoto) {
      changePhoto.addEventListener('click', function () {
        fileInput.click();
      });
    }

    /* ---------------- aspect-locked crop ---------------- */
    var box = { x: 0, y: 0, w: 0, h: 0 };
    var MIN_BOX = 48;

    function applyBox() {
      cropBoxEl.style.left = box.x + 'px';
      cropBoxEl.style.top = box.y + 'px';
      cropBoxEl.style.width = box.w + 'px';
      cropBoxEl.style.height = box.h + 'px';
    }

    function openCrop() {
      if (!state.img || !cropImage) return;
      cropImage.onload = function () {
        var k = cropImage.clientWidth / state.img.naturalWidth;
        box = { x: state.crop.x * k, y: state.crop.y * k, w: state.crop.w * k, h: state.crop.h * k };
        applyBox();
      };
      cropImage.src = state.objectUrl;
      if (cropImage.complete && cropImage.naturalWidth) cropImage.onload();
    }

    var openCropBtn = $('[data-cx-open-crop]');
    if (openCropBtn) openCropBtn.addEventListener('click', openCrop);

    function startDrag(evt, handle) {
      evt.preventDefault();
      var sx = evt.clientX, sy = evt.clientY;
      var start = { x: box.x, y: box.y, w: box.w, h: box.h };
      var W = cropImage.clientWidth, H = cropImage.clientHeight;
      function onMove(e) {
        var dx = e.clientX - sx, dy = e.clientY - sy;
        if (!handle) {
          box.x = Math.max(0, Math.min(start.x + dx, W - start.w));
          box.y = Math.max(0, Math.min(start.y + dy, H - start.h));
        } else {
          // resize from the opposite corner, keeping the aspect ratio
          var ax = handle.indexOf('w') >= 0 ? start.x + start.w : start.x;
          var ay = handle.indexOf('n') >= 0 ? start.y + start.h : start.y;
          var signX = handle.indexOf('w') >= 0 ? -1 : 1;
          var signY = handle.indexOf('n') >= 0 ? -1 : 1;
          var wantW = Math.max(start.w + signX * dx, (start.h + signY * dy) * ASPECT);
          var maxW = Math.min(signX > 0 ? W - ax : ax, (signY > 0 ? H - ay : ay) * ASPECT);
          var nw = Math.max(MIN_BOX, Math.min(wantW, maxW));
          var nh = nw / ASPECT;
          box.w = nw;
          box.h = nh;
          box.x = signX > 0 ? ax : ax - nw;
          box.y = signY > 0 ? ay : ay - nh;
        }
        applyBox();
      }
      function onUp() {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
      }
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    }

    if (cropBoxEl) {
      cropBoxEl.addEventListener('pointerdown', function (e) {
        if (e.target.hasAttribute('data-cx-handle')) return;
        startDrag(e, null);
      });
      cropBoxEl.querySelectorAll('[data-cx-handle]').forEach(function (h) {
        h.addEventListener('pointerdown', function (e) {
          e.stopPropagation();
          startDrag(e, h.getAttribute('data-cx-handle'));
        });
      });
    }

    if (cropConfirm) {
      cropConfirm.addEventListener('click', function () {
        if (!state.img || !cropImage.clientWidth) return;
        var k = state.img.naturalWidth / cropImage.clientWidth;
        state.crop = { x: box.x * k, y: box.y * k, w: box.w * k, h: box.h * k };
        buildSource();
        if (cropModal && typeof cropModal.hide === 'function') cropModal.hide();
        render();
      });
    }

    /* ---------------- rendering ---------------- */
    function setBusy(on, text, asBadge) {
      state.busy = on;
      busy.hidden = !on;
      busy.classList.toggle('cx-photo__busy--badge', !!asBadge);
      if (text) busyText.textContent = text;
      if (regenBtn) {
        regenBtn.disabled = on;
        regenText.textContent = on ? 'Sketching...' : 'Try another style';
      }
      refreshSubmitGate();
    }

    // Effect applied to the (square) photo at a given pixel size. Cached so
    // caption / frame / font changes never re-run an effect.
    function processedPhoto(effect, px) {
      var key = effect + ':' + px;
      if (state.processed[key]) return state.processed[key];
      var out;
      if (effect === 'Oil Painting') {
        out = FX.oilPaint(state.src, { seed: 7, outW: px, outH: px });
      } else {
        var base = FX.resample(state.src, px, px);
        out = effect === 'Retro Film' ? FX.retroFilm(base, { seed: 7 }) : base;
      }
      state.processed[key] = out;
      return out;
    }

    function sketchStyle() {
      return FX.SKETCH_STYLES[state.styleIdx % FX.SKETCH_STYLES.length];
    }

    function buildDesign(height, forPrint) {
      if (mode === 'frame') {
        var L = CMP.polaroidLayout(height);
        return CMP.polaroid({
          photo: processedPhoto(state.effect, L.photo),
          frame: state.frame,
          caption: state.caption,
          font: state.font,
          height: height,
        });
      }
      var H = Math.round(height);
      var W = Math.round(H * CMP.SKETCH_ASPECT);
      var art = FX.sketch(state.src, {
        style: sketchStyle().key,
        seed: state.seed,
        outW: W,
        outH: H,
        workMax: forPrint ? SKETCH_FINAL_WORK : SKETCH_PREVIEW_WORK,
      });
      return CMP.sketchPatch({ art: art, height: H });
    }

    function fontsReady() {
      if (mode !== 'frame' || !document.fonts || !document.fonts.load) return Promise.resolve();
      var f = CMP.FONTS[state.font];
      return document.fonts.load(f.weight + ' 40px ' + f.family, state.caption || 'Aa').catch(function () {});
    }

    function needsHeavyWork() {
      if (mode === 'sketch') return true;
      var L = CMP.polaroidLayout(PREVIEW_FRAME_H);
      return !state.processed[state.effect + ':' + L.photo];
    }

    var arUrl = null;

    function paintToPreview(design) {
      canvas.width = design.width;
      canvas.height = design.height;
      canvas.getContext('2d').drawImage(design, 0, 0);
    }

    // Oil painting is the one slow effect: paint it progressively so the
    // customer watches the strokes go down instead of waiting on a spinner.
    function prepareOilProgressive(token) {
      var L = CMP.polaroidLayout(PREVIEW_FRAME_H);
      var key = 'Oil Painting:' + L.photo;
      if (state.processed[key]) return Promise.resolve();
      return FX.oilPaintProgressive(state.src, { seed: 7, outW: L.photo, outH: L.photo }, function (partial) {
        if (token !== state.renderToken) return;
        paintToPreview(CMP.polaroid({ photo: partial, frame: state.frame, caption: state.caption, font: state.font, height: PREVIEW_FRAME_H }));
      }).then(function (finished) {
        state.processed[key] = finished;
      });
    }

    function render() {
      if (!state.src) return Promise.resolve();
      var token = ++state.renderToken;
      var heavy = needsHeavyWork();
      var progressive = heavy && mode === 'frame' && state.effect === 'Oil Painting';
      if (heavy) setBusy(true, mode === 'sketch' ? BUSY_COPY.sketch : BUSY_COPY[state.effect], progressive);
      return fontsReady()
        .then(function () {
          return heavy ? nextFrame() : null;
        })
        .then(function () {
          if (token !== state.renderToken) return null;
          return progressive ? prepareOilProgressive(token) : null;
        })
        .then(function () {
          if (token !== state.renderToken) return;
          var t0 = performance.now();
          var design = buildDesign(mode === 'frame' ? PREVIEW_FRAME_H : PREVIEW_SKETCH_H, false);
          root.dataset.cxLastRenderMs = String(Math.round(performance.now() - t0));
          paintToPreview(design);
          stage.setAttribute('data-frame', state.frame);
          design.toBlob(function (blob) {
            if (!blob) return;
            if (arUrl) URL.revokeObjectURL(arUrl);
            arUrl = URL.createObjectURL(blob);
            arImg.src = arUrl;
          }, 'image/png');
        })
        .catch(function (err) {
          showError('Could not prepare the preview. Please try a different photo.');
          if (window.console) console.error(err);
        })
        .then(function () {
          if (token === state.renderToken && state.busy) setBusy(false);
        });
    }

    var recomposeTimer = null;
    function scheduleRender(delay) {
      clearTimeout(recomposeTimer);
      recomposeTimer = setTimeout(render, delay || 0);
    }

    /* ---------------- frame-mode controls ---------------- */
    if (mode === 'frame') {
      root.querySelectorAll('[data-cx-frame]').forEach(function (r) {
        r.addEventListener('change', function () {
          state.frame = r.value;
          scheduleRender();
        });
      });
      root.querySelectorAll('[data-cx-font]').forEach(function (r) {
        r.addEventListener('change', function () {
          state.font = r.value;
          scheduleRender();
        });
      });
      root.querySelectorAll('[data-cx-effect]').forEach(function (r) {
        r.addEventListener('change', function () {
          state.effect = r.value;
          if (effectDesc) effectDesc.textContent = EFFECT_COPY[state.effect];
          refreshSubmitGate();
          scheduleRender();
        });
      });
      var chips = root.querySelectorAll('[data-cx-caption-chip]');
      function syncChips() {
        chips.forEach(function (c) {
          c.classList.toggle('is-active', c.getAttribute('data-cx-caption-chip') === state.caption);
        });
        if (captionCount) captionCount.textContent = String(state.caption.length);
      }
      chips.forEach(function (c) {
        c.addEventListener('click', function () {
          captionInput.value = c.getAttribute('data-cx-caption-chip');
          state.caption = captionInput.value;
          syncChips();
          scheduleRender();
        });
      });
      captionInput.addEventListener('input', function () {
        state.caption = captionInput.value.slice(0, 25);
        syncChips();
        scheduleRender(120);
      });
      syncChips();
    }

    /* ---------------- sketch-mode controls ---------------- */
    function syncStyleLabel() {
      if (!styleLabel) return;
      var st = sketchStyle();
      styleLabel.textContent = 'Style ' + ((state.styleIdx % FX.SKETCH_STYLES.length) + 1) + ' of ' + FX.SKETCH_STYLES.length + ': ' + st.label;
    }
    if (regenBtn) {
      regenBtn.addEventListener('click', function () {
        if (!state.src || state.busy) return;
        state.styleIdx += 1;
        state.seed += 1;
        syncStyleLabel();
        render();
      });
      syncStyleLabel();
    }

    /* ---------------- sizes, price, cart (forked from QR) ---------------- */
    function effectTierActive() {
      if (state.prefillUrl) return state.prefillEffectTier;
      return mode === 'frame' && state.effect !== 'None';
    }

    function syncPrice() {
      var native = document.querySelector('[id^="price-"]');
      if (native) native.style.display = 'none';
      var useEffect = effectTierActive();
      document.querySelectorAll('.custom-personalization__size-price[data-price-none]').forEach(function (el) {
        el.textContent = useEffect ? el.dataset.priceEffect : el.dataset.priceNone;
      });
      var radio = root.querySelector('[data-primary-size-radio]:checked');
      if (!radio) {
        ownPrice.hidden = true;
        ownPriceNote.hidden = true;
        return;
      }
      ownPrice.textContent = useEffect ? radio.dataset.priceEffect : radio.dataset.priceNone;
      ownPrice.hidden = false;
      var note = '';
      ownPriceNote.textContent = note;
      ownPriceNote.hidden = !note;
    }

    function hasDesign() {
      return !!(state.src || state.prefillUrl);
    }

    function refreshSubmitGate() {
      var sizeSelected = !!root.querySelector('[data-primary-size-radio]:checked');
      if (!hasDesign()) {
        submitButton.disabled = true;
        submitButtonText.textContent = 'Upload your photo';
      } else if (state.busy) {
        submitButton.disabled = true;
        submitButtonText.textContent = 'Preparing preview...';
      } else if (!sizeSelected) {
        submitButton.disabled = true;
        submitButtonText.textContent = 'Select size';
      } else {
        submitButton.disabled = false;
        submitButtonText.textContent = 'Add to cart';
      }
      if (openMoreSizesButton) openMoreSizesButton.disabled = !(hasDesign() && sizeSelected && !state.busy);
      syncPrice();
    }

    root.querySelectorAll('[data-primary-size-radio]').forEach(function (r) {
      r.addEventListener('change', function () {
        refreshSubmitGate();
        syncModalWithPrimary();
      });
    });

    function syncRowEmphasis(input) {
      var row = input.closest('.custom-personalization__size-row');
      if (row) row.toggleAttribute('data-has-qty', (parseInt(input.value, 10) || 0) > 0);
    }

    function bindSteppers(scope) {
      scope.querySelectorAll('[data-qty-decrease]').forEach(function (btn) {
        if (btn.dataset.cxBound) return;
        btn.dataset.cxBound = '1';
        btn.addEventListener('click', function () {
          var input = btn.closest('.custom-personalization__qty').querySelector('input');
          var min = parseInt(input.min, 10) || 0;
          input.value = Math.max(min, (parseInt(input.value, 10) || 0) - 1);
          syncRowEmphasis(input);
          if (input === primaryQtyInput) syncModalWithPrimary();
        });
      });
      scope.querySelectorAll('[data-qty-increase]').forEach(function (btn) {
        if (btn.dataset.cxBound) return;
        btn.dataset.cxBound = '1';
        btn.addEventListener('click', function () {
          var input = btn.closest('.custom-personalization__qty').querySelector('input');
          input.value = (parseInt(input.value, 10) || 0) + 1;
          syncRowEmphasis(input);
          if (input === primaryQtyInput) syncModalWithPrimary();
        });
      });
      scope.querySelectorAll('[data-qty-input]').forEach(function (input) {
        if (input.dataset.cxBound) return;
        input.dataset.cxBound = '1';
        input.addEventListener('input', function () {
          syncRowEmphasis(input);
        });
      });
    }
    bindSteppers(root);
    if (moreSizesModal) bindSteppers(moreSizesModal);

    function syncModalWithPrimary() {
      if (!moreSizesModal) return;
      var radio = root.querySelector('[data-primary-size-radio]:checked');
      var key = radio ? radio.dataset.variantIdNone : null;
      moreSizesModal.querySelectorAll('[data-qty-input]').forEach(function (input) {
        var row = input.closest('.custom-personalization__size-row');
        var isPrimary = key && input.dataset.variantIdNone === key;
        var wasPrimary = row && row.hasAttribute('data-linked-to-primary');
        if (isPrimary) {
          input.value = primaryQtyInput.value;
          input.disabled = true;
          if (row) row.setAttribute('data-linked-to-primary', '');
        } else {
          if (wasPrimary) {
            input.value = 0;
            if (row) row.removeAttribute('data-has-qty');
          }
          if (!input.hasAttribute('data-sold-out')) input.disabled = false;
          if (row) row.removeAttribute('data-linked-to-primary');
        }
      });
    }
    if (openMoreSizesButton) openMoreSizesButton.addEventListener('click', syncModalWithPrimary);
    primaryQtyInput.addEventListener('input', syncModalWithPrimary);

    if (moreSizesModal) {
      var modalSubmit = moreSizesModal.querySelector('[data-modal-submit]');
      if (modalSubmit) {
        modalSubmit.addEventListener('click', function () {
          submitButton.click();
        });
      }
    }

    function collectSelections() {
      var bySize = {};
      var radio = root.querySelector('[data-primary-size-radio]:checked');
      var key = radio ? radio.dataset.variantIdNone : null;
      if (radio) {
        var q = parseInt(primaryQtyInput.value, 10) || 0;
        if (q > 0) bySize[key] = { qty: q, none: radio.dataset.variantIdNone, effect: radio.dataset.variantIdEffect, size: parseFloat(radio.dataset.sizeIn) };
      }
      if (moreSizesModal) {
        moreSizesModal.querySelectorAll('[data-qty-input]').forEach(function (input) {
          var k = input.dataset.variantIdNone;
          if (k === key) return;
          var q2 = parseInt(input.value, 10) || 0;
          if (q2 > 0) bySize[k] = { qty: q2, none: input.dataset.variantIdNone, effect: input.dataset.variantIdEffect, size: parseFloat(input.dataset.sizeIn) };
        });
      }
      var useEffect = effectTierActive();
      return Object.keys(bySize).map(function (k) {
        var r = bySize[k];
        return { variantId: useEffect ? r.effect : r.none, quantity: r.qty, size: r.size };
      });
    }

    function showError(msg) {
      errorText.textContent = msg;
      errorWrapper.hidden = false;
    }
    function clearError() {
      errorWrapper.hidden = true;
      errorText.textContent = '';
    }

    function setLoading(on, text) {
      submitButton.toggleAttribute('aria-disabled', on);
      submitButton.classList.toggle('loading', on);
      var sp = submitButton.querySelector('.loading__spinner');
      if (sp) sp.classList.toggle('hidden', !on);
      if (text) submitButtonText.textContent = text;
    }

    function properties() {
      if (state.prefillUrl) return { Design: 'Same as your earlier design' };
      if (mode === 'frame') {
        return {
          Frame: state.frame,
          Caption: state.caption.trim() || '(no caption)',
          'Caption Font': CMP.FONTS[state.font].label,
          Effect: state.effect,
        };
      }
      return { 'Sketch Style': sketchStyle().label + ' (variation ' + state.seed + ')' };
    }

    // Print file: biggest selected size x 300 dpi (biggest dimension).
    function buildPrintFile(selections) {
      var maxIn = 0;
      selections.forEach(function (s) {
        if (s.size > maxIn) maxIn = s.size;
      });
      var heightPx = Math.round(Math.max(maxIn, 2) * PRINT_DPI);
      return fontsReady()
        .then(nextFrame)
        .then(function () {
          var design = buildDesign(heightPx, true);
          return new Promise(function (resolve, reject) {
            design.toBlob(function (blob) {
              if (!blob) reject(new Error('Could not prepare your print file. Please try again.'));
              else resolve(new File([blob], mode === 'frame' ? 'photo-frame.png' : 'charcoal-sketch.png', { type: 'image/png' }));
            }, 'image/png');
          });
        });
    }

    function postJson(body) {
      return fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then(function (r) {
        return r.json();
      });
    }

    function postFirstWithFile(sel, file, props, sections) {
      var fd = new FormData();
      fd.append('id', sel.variantId);
      fd.append('quantity', sel.quantity);
      fd.append('properties[Custom Image]', file);
      Object.keys(props).forEach(function (k) {
        fd.append('properties[' + k + ']', props[k]);
      });
      if (sections) {
        fd.append('sections', sections);
        fd.append('sections_url', window.location.pathname);
      }
      return fetch(window.routes.cart_add_url, { method: 'POST', headers: { Accept: 'application/json' }, body: fd }).then(function (r) {
        return r.json();
      });
    }

    function renderCartSections(response) {
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart && typeof cart.renderContents === 'function' && response.sections) cart.renderContents(response);
      var drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.remove('is-empty');
      var items = document.querySelector('cart-drawer-items');
      if (items) items.classList.remove('is-empty');
    }

    function afterAdded() {
      // Keep the design on screen (people often add a second size or
      // another copy); just reset quantities.
      primaryQtyInput.value = 1;
      if (moreSizesModal) {
        moreSizesModal.querySelectorAll('[data-qty-input]').forEach(function (input) {
          input.value = 0;
          var row = input.closest('.custom-personalization__size-row');
          if (row) row.removeAttribute('data-has-qty');
        });
        if (typeof moreSizesModal.hide === 'function') moreSizesModal.hide();
      }
    }

    submitButton.addEventListener('click', function () {
      clearError();
      if (!hasDesign() || state.busy) return;
      var selections = collectSelections();
      if (!selections.length) {
        showError('Please choose a quantity for at least one size.');
        return;
      }
      var sections = 'cart-drawer,cart-icon-bubble';
      var props = properties();
      var first = selections[0];
      var rest = selections.slice(1);
      setLoading(true, state.prefillUrl ? 'Adding...' : 'Preparing your print file...');
      submitButton.disabled = true;

      var filePromise = state.prefillUrl ? Promise.resolve(null) : buildPrintFile(selections);
      filePromise
        .then(function (file) {
          submitButtonText.textContent = 'Adding...';
          var firstReq = file
            ? postFirstWithFile(first, file, props, rest.length ? null : sections)
            : postJson(
                Object.assign(
                  { id: first.variantId, quantity: first.quantity, properties: Object.assign({ 'Custom Image': state.prefillUrl }, props) },
                  rest.length ? {} : { sections: sections, sections_url: window.location.pathname }
                )
              );
          return firstReq.then(function (res) {
            if (res.status) throw new Error(res.description || res.message || 'Could not add to cart.');
            if (!rest.length) return res;
            var bulkProps = Object.assign({ 'Custom Image': res.properties['Custom Image'] }, props);
            return postJson({
              items: rest.map(function (s) {
                return { id: s.variantId, quantity: s.quantity, properties: bulkProps };
              }),
              sections: sections,
              sections_url: window.location.pathname,
            }).then(function (r2) {
              if (r2.status || r2.description) throw new Error(r2.description || r2.message || 'Could not add all sizes to cart.');
              return r2;
            });
          });
        })
        .then(function (finalRes) {
          renderCartSections(finalRes);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cx-photo', productVariantId: first.variantId, cartData: finalRes });
          }
          afterAdded();
        })
        .catch(function (err) {
          showError((err && err.message) || 'Something went wrong adding this to your cart. Please try again.');
        })
        .then(function () {
          setLoading(false);
          refreshSubmitGate();
        });
    });

    /* ---------------- edit from cart ---------------- */
    var params = new URLSearchParams(window.location.search);
    if (params.get('edit_variant')) {
      var vid = params.get('edit_variant');
      var match = root.querySelector('[data-primary-size-radio][data-variant-id-none="' + vid + '"]');
      var matchEffect = root.querySelector('[data-primary-size-radio][data-variant-id-effect="' + vid + '"]');
      var radio = match || matchEffect;
      if (radio) radio.checked = true;
      if (params.get('edit_qty')) primaryQtyInput.value = params.get('edit_qty');
      if (params.get('edit_image')) {
        state.prefillUrl = params.get('edit_image');
        state.prefillEffectTier = !match && !!matchEffect && mode === 'frame';
        previewWrap.hidden = false;
        canvas.hidden = true;
        prefillImg.src = state.prefillUrl;
        prefillImg.hidden = false;
        arImg.src = state.prefillUrl;
        dropLabel.textContent = 'Upload a new photo to start again';
      }
    }

    refreshSubmitGate();

    // test hook for the verification harness (no effect on shoppers)
    root.cxPhotoDebug = { state: state, render: render, buildPrintFile: buildPrintFile, collectSelections: collectSelections };
  }

  function boot() {
    document.querySelectorAll('[data-cx-photo]').forEach(function (root) {
      if (root.dataset.cxInit) return;
      root.dataset.cxInit = '1';
      init(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
